import { SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS } from './suitabilityOptions'
import type {
  VanDerValkSolarRampAnchorType,
  VanDerValkSolarRampFixation,
  VanDerValkSolarRampFoundationType,
} from './solarRampProfiles'

const SOLAR_RAMP_ROOF_MATERIAL_LABEL_KEYS: Record<string, string> = {
  WOODEN_ROOF_FLOOR: 'mount_vdv_roof_material_wooden_roof_floor',
  METAL_ROOF_FLOOR: 'mount_vdv_roof_material_metal_roof_floor',
  MASSIVE_CONCRETE_FLOOR: 'mount_vdv_roof_material_massive_concrete_floor',
  HOLLOW_CORE_SLAB_FLOOR: 'mount_vdv_roof_material_hollow_core_slab_floor',
  AERATED_CONCRETE_ROOF_FLOOR: 'mount_vdv_roof_material_aerated_concrete_roof_floor',
}

const SOLAR_RAMP_FASTENER_TYPE_LABEL_KEYS: Record<string, string> = {
  ROOF_SCREWS: 'mount_vdv_fastener_type_roof_screws',
  TOGGLE_ANCHOR: 'mount_vdv_fastener_type_toggle_anchor',
  CONCRETE_ANCHOR: 'mount_vdv_fastener_type_concrete_anchor',
}

const SOLAR_RAMP_FOUNDATION_TYPE_LABEL_KEYS: Record<VanDerValkSolarRampFoundationType, string> = {
  MASS_BLOCKS: 'mount_vdv_foundation_mass_blocks',
  TILE_CARRIERS: 'mount_vdv_foundation_tile_carriers',
}

export function getSolarRampFoundationTypeLabelKey(foundationType: VanDerValkSolarRampFoundationType): string {
  return SOLAR_RAMP_FOUNDATION_TYPE_LABEL_KEYS[foundationType]
}

export function getSolarRampRoofMaterialLabelKey(material: string): string {
  return SOLAR_RAMP_ROOF_MATERIAL_LABEL_KEYS[material] ?? material
}

export function getSolarRampFastenerTypeLabelKey(fastenerType: string): string {
  return SOLAR_RAMP_FASTENER_TYPE_LABEL_KEYS[fastenerType] ?? fastenerType
}

export function getSolarRampRoofConstructionHeightLabel(heightMm: number): string {
  return `${heightMm} mm`
}

export function isSolarRampAnchorFieldsComplete(vd: {
  flatRoofFixation?: unknown
  anchorType?: unknown
  fastenerType?: unknown
  roofMaterial?: unknown
  roofConstructionHeight?: unknown
}): boolean {
  if (vd.flatRoofFixation !== 'ANCHORING') {
    return true
  }
  const anchorType = vd.anchorType
  if (anchorType !== 'VALK_SOLAR_FIX' && anchorType !== 'OTHER') {
    return false
  }
  if (anchorType === 'OTHER') {
    return true
  }
  const fastenerType = typeof vd.fastenerType === 'string' ? vd.fastenerType : ''
  const roofMaterial = typeof vd.roofMaterial === 'string' ? vd.roofMaterial : ''
  const heightRaw = vd.roofConstructionHeight
  const height =
    typeof heightRaw === 'number'
      ? heightRaw
      : typeof heightRaw === 'string'
        ? parseInt(heightRaw.trim(), 10)
        : NaN
  if (!fastenerType || !roofMaterial || !Number.isFinite(height)) {
    return false
  }
  const allowed = SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS[roofMaterial]?.[fastenerType]
  return Array.isArray(allowed) && allowed.includes(height)
}

export function getSolarRampFastenerTypeOptions(roofMaterial: string | undefined): string[] {
  if (!roofMaterial) {
    return []
  }
  const entry = SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS[roofMaterial]
  if (!entry) {
    return []
  }
  return Object.keys(entry)
}

export function getSolarRampRoofMaterialOptions(fastenerType: string | undefined): string[] {
  if (!fastenerType) {
    return Object.keys(SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS)
  }
  return Object.keys(SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS).filter((material) =>
    Object.prototype.hasOwnProperty.call(SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS[material], fastenerType)
  )
}

export function getSolarRampRoofConstructionHeightOptions(
  roofMaterial: string | undefined,
  fastenerType: string | undefined
): number[] {
  if (!roofMaterial || !fastenerType) {
    return []
  }
  return SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS[roofMaterial]?.[fastenerType] ?? []
}

export const SOLAR_RAMP_FIXATION_OPTIONS: { label: string; value: VanDerValkSolarRampFixation }[] = [
  { label: 'mount_vdv_solar_ramp_fixation_ballasting', value: 'BALLASTING' },
  { label: 'mount_vdv_solar_ramp_fixation_anchoring', value: 'ANCHORING' },
]

export const SOLAR_RAMP_ANCHOR_TYPE_OPTIONS: { label: string; value: VanDerValkSolarRampAnchorType }[] = [
  { label: 'mount_vdv_anchor_type_valk_solar_fix', value: 'VALK_SOLAR_FIX' },
  { label: 'mount_vdv_anchor_type_other', value: 'OTHER' },
]
